import axios from "axios";
import { toast } from "react-toastify";
import { toastProperties } from "../commons/Toast/Toaster";
import { decryptData } from "../commons/Decode";

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 300000,
  headers: {
    "Content-Type": "application/json",
    "X-encryption-bypass-token":
      process.env.REACT_APP_ENCRYPTION_BYPASS_TOKEN || "",
  },
});

const applyDecrypt = async (responseData) => {
  try {
    // Case 1: Entire response encrypted directly
    if (typeof responseData === "string") {
      return await decryptData(responseData);
    }

    // Case 2: Wrapped encrypted response
    if (responseData?.data && typeof responseData.data === "string") {
      return await decryptData(responseData.data);
    }

    // Already decrypted
    return responseData;
  } catch (error) {
    return responseData;
  }
};

// Keywords whose URLs are blocked from making API calls
const BLOCKED_URL_KEYWORDS = ["payment", "transaction", "wallet", "contract"];

const isBlockedUrl = (url = "") => {
  const lower = url.toLowerCase();
  return BLOCKED_URL_KEYWORDS.some((keyword) => lower.includes(keyword));
};

//Request interceptor
api.interceptors.request.use(
  (config) => {
    if (isBlockedUrl(config.url)) {
      const controller = new AbortController();
      controller.abort();
      config.signal = controller.signal;
      return config;
    }

    const token = localStorage.getItem("OTC_V2_TOKEN");
    if (token) {
      config.headers.Authorization = ` ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  async (response) => {
    try {
      // Skip blob downloads
      if (response.config.responseType === "blob") {
        return response;
      }

      // Decrypt complete response
      response.data = await applyDecrypt(response.data);

      return response;
    } catch (err) {
      return response;
    }
  },
  async (error) => {
    if (error?.response?.data) {
      try {
        error.response.data = await applyDecrypt(error.response.data);
      } catch (decryptErr) {}
    }

    if (error?.response?.status === 401) {
      if (!error.config?.skipAuthRedirect) {
        localStorage.clear();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);
// GET request
export const getApi = async (
  url,
  headers = {},
  showSuccess = false,
  responseType = "json",
  config = {},
) => {
  try {
    const axiosConfig = {
      headers,
      responseType,
      ...config,
    };

    // Apply transformResponse ONLY for blobs
    if (responseType === "blob") {
      axiosConfig.transformResponse = (r) => r;
    }

    const response = await api.get(url, axiosConfig);
    return response;
  } catch (error) {
    return error.response || error;
  }
};

// POST request
export const postApi = async (
  url,
  data = {},
  config = {},
  showSuccess = true,
  responseType = "json", // Add responseType param
) => {
  try {
    const isFormData = data instanceof FormData;

    const axiosConfig = {
      ...config,
      headers: {
        ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        ...(config.headers || {}),
      },
      responseType, // Add responseType to config
    };

    // Apply transformResponse ONLY for blobs
    if (responseType === "blob") {
      axiosConfig.transformResponse = (r) => r;
    }

    const response = await api.post(url, data, axiosConfig);

    if (response?.data?.code === 200) {
      //toast.success(response.data.message, toastProperties);
      return response;
    } else {
      // toast.error(response?.data?.error, toastProperties);
      return response;
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      "Failed to create data";
    toast.error(errorMessage, toastProperties);
    return error;
  }
};

// PUT request
export const putApi = async (
  url,
  data = {},
  headers = {},
  showSuccess = true,
) => {
  try {
    const response = await api.put(url, data, { headers });
    if (showSuccess && response.data?.message) {
      //toast.success(response.data.message, toastProperties);
    }
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to update data";
    toast.error(errorMessage, toastProperties);
  }
};

// DELETE request
export const deleteApi = async (
  url,
  data = null,
  headers = {},
  showSuccess = true,
) => {
  try {
    const config = { headers };
    if (data !== null) {
      config.data = data;
    }
    const response = await api.delete(url, config);
    if (showSuccess && response.data?.message) {
      // toast.success(response.data.message, toastProperties);
      // toast.success(response.data.message, toastProperties);
    }
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to delete data";
    toast.error(errorMessage, toastProperties);
    throw error; // Re-throw to allow caller to handle
  }
};

export const patchApi = async (
  url,
  data = {},
  headers = {},
  showSuccess = true,
) => {
  try {
    const response = await api.patch(url, data, { headers });
    if (showSuccess && response.data?.message) {
      //toast.success(response.data.message, toastProperties);
    }
    return response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to update data";
    toast.error(errorMessage, toastProperties);
  }
};

// Success message handler
export const showSuccessMessage = (message) => {
  //toast.success(message, toastProperties);
};

// Error message handler
export const showErrorMessage = (message) => {
  //toast.error(message, toastProperties);
};
